import { CircularProgress } from '@material-ui/core';
import { modeUIContext, setIsEditingForm, showSuccessNotification } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import produce from 'immer';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BaseHomeInfoProps, BaseHomeInfoUpdatableInputs, capitalizeAll, FieldInputType, GenericInfoComp } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById, selectSudoProjectAccessKey } from '@moderepo/mode-data-state-ritz';
import { fetchHomeById, userAppDataContext, selectHomeById, updateHome, createHome, clearProjectHomes, clearUserHomes }
    from '@moderepo/mode-data-state-user-app';
import { useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';


interface InputErrors {
    name: string | null;
}


interface HomeInfoContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly userId?: number | undefined;
    readonly homeId?: number;                                   // will be undefined if this container is used for creating new home
    readonly newHomeSettings?: {                                // Only available if this container is used for creating new home
        readonly onNewHomeCreated: ()=> void;
        readonly onNewHomeCanceled: ()=> void;
    }

    // This container used HomeInfo component to display user info and UserInfo component has its own customizable props which can be
    // set through this props.
    readonly infoCompProps?: {
        readonly className?: BaseHomeInfoProps['className'] | undefined;
        readonly formProps?: BaseHomeInfoProps['formProps'] | undefined;
    } | undefined;
}


/**
 * This container is for displaying a HomeInfoComp and provide all the logic for a HomeInfoComp e.g. fetching data and updating data.
 * Because we need to show HomeInfoComp in multiple places, it is better to wrap the HomeComponent UI and login into 1 container
 * and use this container in places where we want to show HomeInfoComp
 */
export const HomeInfoContainer = (props: HomeInfoContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    const project = selectProjectById(dataState, props.projectId);
    const projectKey = selectSudoProjectAccessKey(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const selectedHome = props.homeId ? selectHomeById(appDataState, props.homeId) : undefined;
    const [inputErrors, setInputErrors] = useState<InputErrors>({
        name: null,
    });
    const isNewHome = props.homeId === undefined;



    /**
     * This useEffect used for loading home data on component init
     */
    useEffect(() => {
        if (props.homeId && !selectedHome) {
            appDataDispatch(fetchHomeById(props.homeId)).catch(() => { /* ignore error */ });
        }
    }, [props.userId, props.homeId, selectedHome, appDataDispatch]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.homeId is undefined which is when user want to create a Home
     */
    useEffect(() => {
        if (isNewHome) {
            onChangeEditingMode(true);
        }
    }, [isNewHome, onChangeEditingMode]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({
            name: null,
        });

        // also change global edit mode
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onHomeInfoFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [inputErrors]);


    const onCreateNewHome = useCallback(async (name: string | undefined) => {
        const newInputErrors: InputErrors = ({
            name: null,
        });

        if (!name) {
            newInputErrors.name = 'error.input.name.empty';
        }

        setInputErrors(newInputErrors);

        if (!newInputErrors.name && name) {
            try {
                await appDataDispatch(createHome(name));

                // Clear the projectHomes data in the data state because the current list of Project homes are no longer up to date
                if (props.userId) {
                    await appDataDispatch(clearUserHomes(props.userId));
                }
                await appDataDispatch(clearProjectHomes(props.projectId));

                closeEditing();
                uiDispatch(showSuccessNotification('notif.generic_item_created_successfully', { term: 'terms.home' }));

                if (props.newHomeSettings) {
                    props.newHomeSettings.onNewHomeCreated();
                }
            } catch (error) {
                // ignore error
            }
        }
    }, [appDataDispatch, props.userId, props.projectId, props.newHomeSettings, closeEditing, uiDispatch]);


    /**
     * Handler for when the user click Save input from the Home form.
     * If we are in creating new home mode, call createNewHome. Else update the current home
     */
    const onHomeInfoFormSubmit = useCallback(async (inputs: BaseHomeInfoUpdatableInputs) => {
        if (!project) {
            return;
        }

        const { name, deactivated } = inputs;

        if (isNewHome) {
            onCreateNewHome(name);
        } else if (selectedHome) {

            const newInputErrors: InputErrors = ({
                name: null,
            });

            // Validate inputs
            if (!name) {
                newInputErrors.name = 'error.input.name.empty';
            }

            // Update input errors if there are any
            setInputErrors(newInputErrors);

            if (!newInputErrors.name) {
                // Inputs validated successfully. Now make API call to update the home if one of the home's attributes is modified.
                if (name && (name !== selectedHome.name || deactivated !== Boolean(selectedHome.deactivated))) {
                    try {
                        // dispatch an action to update the home name and/or deactivate state
                        await appDataDispatch(updateHome(selectedHome.id, { name, deactivated }, projectKey?.token));

                        // Clear the projectHomes data in the data state because the current list of Project homes are no longer up to date
                        if (props.userId) {
                            await appDataDispatch(clearUserHomes(props.userId));
                        }
                        await appDataDispatch(clearProjectHomes(props.projectId));

                        // Reload home
                        await appDataDispatch(fetchHomeById(selectedHome.id));

                        uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'terms.home' }));
                        closeEditing();
                    } catch (error) {
                        /* ignore error */
                    }
                } else {
                    // newName and 'deactivated' is the same so no need to make api call
                    closeEditing();
                }
            }
        }
    }, [project, isNewHome, selectedHome, onCreateNewHome, appDataDispatch, projectKey?.token, props.userId, props.projectId, uiDispatch,
        closeEditing]);



    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (props.newHomeSettings !== undefined) {
            props.newHomeSettings.onNewHomeCanceled();
        }
    }, [props.newHomeSettings, closeEditing]);



    /**
     * Create the input settings for the HomeInfoComp and cache it
     */
    const inputsSettings = useMemo((): BaseHomeInfoProps['fields'] => {
        const defaultSettings: BaseHomeInfoProps['fields'] = {
            name: {
                label       : capitalizeAll(trans('label.name')),
                displayValue: selectedHome?.name,
                value       : selectedHome?.name,
                editable    : true,
                error       : inputErrors.name ? trans(inputErrors.name) : '',
            },
        };

        if (!isNewHome && selectedHome) {
            // If homeId is specified then that mean we are showing Home info for a specific home
            return {
                id: {
                    label       : capitalizeAll(trans('label.id')),
                    displayValue: selectedHome.id.toString(),
                    value       : selectedHome.id,
                },
                deactivated: {
                    label       : capitalizeAll(trans('label.status')),
                    displayValue: selectedHome.deactivated ? capitalizeAll(trans('label.deactivated')) : capitalizeAll(trans('label.active')),
                    value       : selectedHome.deactivated ?? false,
                    editable    : true,
                    controlType : FieldInputType.SELECT,
                    options     : [{
                        value: false,
                        label: capitalizeAll(trans('label.active')),
                    }, {
                        value: true,
                        label: capitalizeAll(trans('label.deactivated')),
                    }],
                },
                ...defaultSettings,
                creationTime: {
                    label       : capitalizeAll(trans('label.date_created')),
                    displayValue: getDateTimeAndElapsedTime(selectedHome.creationTime, trans),
                    value       : selectedHome.creationTime,
                },
            };
        }

        return defaultSettings;
    }, [isNewHome, selectedHome, trans, inputErrors.name]);


    return (
        <>
            {project && (selectedHome || isNewHome) && (
                <GenericInfoComp<BaseHomeInfoProps>
                    key={selectedHome?.id}
                    className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
                    formProps={props.infoCompProps?.formProps}
                    isEditing={isEditing}
                    title={capitalizeAll(trans(isNewHome ? 'label.add_home' : 'label.home_info'))}
                    icon={trans('icons.home')}
                    fields={inputsSettings}
                    actions={{
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : isNewHome ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                            onClick: onHomeInfoFormSubmit,
                        },
                    }}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onHomeInfoFormInputChange}
                />
            )}

            {(!project || (!isNewHome && !selectedHome)) && (
                <CircularProgress />
            )}
        </>

    );
};
